import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { styled } from 'styled-components';
import SceneManager from './components/SceneManager';
import Navigation from './components/Navigation/Navigation';
import CinematicParticles from './components/UI/CinematicParticles';
import VideoBackground from './components/UI/VideoBackground';
import SpotlightEffect from './components/UI/SpotlightEffect';
import Floating3DElements from './components/UI/Floating3DElements';
import EasterEggs from './components/UI/EasterEggs';
import CustomCursor from './components/UI/CustomCursor';
import './App.css';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #000;
  position: relative;
`;

const ScrollContainer = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
`;

const SectionsContainer = styled.div`
  display: flex;
  width: 500vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  transition: transform 1.5s cubic-bezier(0.25, 0.1, 0.25, 1);
`;


const Title = styled.h1`
  position: fixed;
  top: 2rem;
  left: 2rem;
  z-index: 1000;
  color: #fff;
  font-family: 'Arial', sans-serif;
  font-size: 1.5rem;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  .highlight {
    color: #00ff88;
    font-weight: 600;
    text-shadow: 0 0 20px #00ff88;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    top: 1rem;
    left: 1rem;
  }
`;

function App() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionsContainerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<HTMLDivElement>(null);
  const currentSectionRef = useRef(0);
  const isAnimatingRef = useRef(false);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const sectionsContainer = sectionsContainerRef.current;
    const app = appRef.current;
    if (!scrollContainer || !sectionsContainer || !app) return;

    const totalSections = 5;
    let currentSection = 0;

    // Set initial positions
    gsap.set(sectionsContainer, { x: 0 });
    
    // Set initial states for all scenes
    gsap.set('.scene', {
      opacity: 1,
      x: 0,
      rotationY: 0,
      scale: 1
    });

    const animateToSection = (sectionIndex: number, direction: 'next' | 'prev') => {
      if (isAnimatingRef.current) return;
      
      isAnimatingRef.current = true;
      currentSection = sectionIndex;
      currentSectionRef.current = sectionIndex;

      // Calculate position with curve effect
      const targetX = -sectionIndex * 100;
      
      // Create timeline for smooth curve animation
      const tl = gsap.timeline({
        onComplete: () => {
          isAnimatingRef.current = false;
        }
      });

      // Animate the container with curve effect
      tl.to(sectionsContainer, {
        x: `${targetX}vw`,
        duration: 1.8,
        ease: "power3.inOut"
      });

      // Animate scenes with staggered curve effect
      const scenes = gsap.utils.toArray('.scene');
      scenes.forEach((scene: any, index) => {
        const isCurrentSection = index === sectionIndex;
        const distance = Math.abs(index - sectionIndex);
        
        if (isCurrentSection) {
          // Current section animation
          tl.to(scene, {
            opacity: 1,
            scale: 1,
            rotationY: 0,
            x: 0,
            duration: 1.2,
            ease: "power2.out"
          }, "-=1.5");
        } else {
          // Other sections with curve effect
          const curveOffset = direction === 'next' ? -50 : 50;
          tl.to(scene, {
            opacity: 0.3,
            scale: 0.9,
            rotationY: distance <= 1 ? curveOffset * 0.3 : 0,
            x: distance <= 1 ? curveOffset * 0.5 : 0,
            duration: 1,
            ease: "power2.inOut"
          }, "-=1.5");
        }
      });

      // Animate parallax elements with curve
      const parallaxElements = gsap.utils.toArray('.holographic-interface, .main-title, .sub-title, .welcome-text');
      parallaxElements.forEach((element: any, i) => {
        const parentSection = element.closest('.section-wrapper');
        if (parentSection) {
          const sectionIndex = Array.from(parentSection.parentNode?.children || []).indexOf(parentSection);
          const isActive = sectionIndex === currentSection;
          
          if (isActive) {
            tl.to(element, {
              x: 0,
              rotationY: 0,
              opacity: 1,
              duration: 1.2,
              ease: "power2.out"
            }, "-=1.2");
          }
        }
      });
    };

    const handleWheelScroll = (e: WheelEvent) => {
      e.preventDefault();
      
      if (isAnimatingRef.current) return;

      const direction = e.deltaY > 0 ? 'next' : 'prev';
      let targetSection = currentSection;

      if (direction === 'next' && currentSection < totalSections - 1) {
        targetSection = currentSection + 1;
      } else if (direction === 'prev' && currentSection > 0) {
        targetSection = currentSection - 1;
      }

      if (targetSection !== currentSection) {
        animateToSection(targetSection, direction);
      }
    };

    // Enhanced title parallax with curve effect
    const titleElement = app.querySelector('.app-title');
    if (titleElement) {
      gsap.set(titleElement, { x: 0, rotationZ: 0 });
    }

    // Add event listeners - attach to document to ensure it works everywhere
    document.addEventListener('wheel', handleWheelScroll, { passive: false });

    // Initial animation
    gsap.fromTo('.scene', 
      { opacity: 0, x: 50, rotationY: 10 },
      { opacity: 1, x: 0, rotationY: 0, duration: 1.5, ease: "power3.out", stagger: 0.1 }
    );

    // Store animation function for navigation
    (window as any).animateToSection = animateToSection;

    // Cleanup
    return () => {
      document.removeEventListener('wheel', handleWheelScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleScroll = (direction: 'up' | 'down') => {
    const currentSection = currentSectionRef.current;
    const totalSections = 5;
    
    let targetSection = currentSection;
    
    if (direction === 'down' && currentSection < totalSections - 1) {
      targetSection = currentSection + 1;
    } else if (direction === 'up' && currentSection > 0) {
      targetSection = currentSection - 1;
    }
    
    if (targetSection !== currentSection && (window as any).animateToSection) {
      (window as any).animateToSection(targetSection, direction === 'down' ? 'next' : 'prev');
    }
  };

  return (
    <AppContainer ref={appRef}>
      <VideoBackground />
      <Floating3DElements />
      <CinematicParticles />
      <SpotlightEffect />
      
      <Title className="app-title">
        THE HALL OF <span className="highlight">ZERO LIMITS</span>
      </Title>
      
      <ScrollContainer ref={scrollContainerRef}>
        <SectionsContainer ref={sectionsContainerRef}>
          <SceneManager />
        </SectionsContainer>
      </ScrollContainer>
      
      <div className="navigation">
        <Navigation onScroll={handleScroll} />
      </div>
      
      <EasterEggs />
      <CustomCursor />
    </AppContainer>
  );
}

export default App;
